import { useEffect, useRef, useState } from "react";
import { DayPicker } from "react-day-picker";
import {
  format, startOfDay, endOfDay, subDays, startOfWeek, endOfWeek, subWeeks,
  startOfMonth, endOfMonth, subMonths,
} from "date-fns";
import { Calendar as CalIcon, ChevronDown, Clock, X } from "lucide-react";

const PRESETS = [
  ["today", "Today", () => [startOfDay(new Date()), endOfDay(new Date())]],
  ["yesterday", "Yesterday", () => { const d = subDays(new Date(), 1); return [startOfDay(d), endOfDay(d)]; }],
  ["this_week", "This week", () => [startOfWeek(new Date()), endOfWeek(new Date())]],
  ["last_week", "Last week", () => { const d = subWeeks(new Date(), 1); return [startOfWeek(d), endOfWeek(d)]; }],
  ["this_month", "This month", () => [startOfMonth(new Date()), endOfMonth(new Date())]],
  ["last_month", "Last month", () => { const d = subMonths(new Date(), 1); return [startOfMonth(d), endOfMonth(d)]; }],
  ["custom", "Custom Range", null],
];

const fmtLabel = (d) => format(d, "MMM d, yyyy HH:mm").toUpperCase();
const pad = (n) => String(n).padStart(2, "0");

function withTime(day, t) {
  const d = new Date(day);
  const h = (t.h % 12) + (t.ap === "PM" ? 12 : 0);
  d.setHours(h, t.m, t.s, t.s === 59 ? 999 : 0);
  return d;
}
const timeOf = (d) => ({ h: d.getHours() % 12 || 12, m: d.getMinutes(), s: d.getSeconds(), ap: d.getHours() >= 12 ? "PM" : "AM" });

function TimeSelect({ value, onChange, testid }) {
  const sel = "bg-slate-950 border border-slate-700 rounded px-1 py-1 text-sm text-slate-200 focus:border-indigo-500 outline-none";
  const set = (k) => (e) => onChange({ ...value, [k]: k === "ap" ? e.target.value : Number(e.target.value) });
  const nums = (from, to) => Array.from({ length: to - from + 1 }, (_, i) => from + i);
  return (
    <div className="flex items-center gap-1" data-testid={testid}>
      <Clock className="w-4 h-4 text-slate-400 mr-1" />
      <select className={sel} value={value.h} onChange={set("h")} data-testid={`${testid}-h`}>{nums(1, 12).map((n) => <option key={n} value={n}>{pad(n)}</option>)}</select>
      <span className="text-slate-500">:</span>
      <select className={sel} value={value.m} onChange={set("m")} data-testid={`${testid}-m`}>{nums(0, 59).map((n) => <option key={n} value={n}>{pad(n)}</option>)}</select>
      <span className="text-slate-500">:</span>
      <select className={sel} value={value.s} onChange={set("s")} data-testid={`${testid}-s`}>{nums(0, 59).map((n) => <option key={n} value={n}>{pad(n)}</option>)}</select>
      <select className={sel} value={value.ap} onChange={set("ap")} data-testid={`${testid}-ap`}><option>AM</option><option>PM</option></select>
    </div>
  );
}

const dayClasses = {
  months: "flex flex-col sm:flex-row gap-6",
  month: "space-y-3",
  caption: "flex justify-center relative items-center h-8",
  caption_label: "text-sm font-semibold text-slate-100",
  nav: "flex items-center",
  nav_button: "h-7 w-7 inline-flex items-center justify-center rounded text-slate-300 hover:bg-slate-800",
  nav_button_previous: "absolute left-0",
  nav_button_next: "absolute right-0",
  table: "border-collapse",
  head_cell: "w-9 text-[12px] font-semibold text-slate-300",
  cell: "p-0 text-center",
  day: "w-9 h-8 text-sm text-slate-200 rounded hover:bg-slate-700 transition-colors",
  day_selected: "!bg-indigo-600 !text-white",
  day_range_middle: "!bg-indigo-600/30 !text-slate-100 !rounded-none",
  day_today: "ring-1 ring-indigo-400/60",
  day_outside: "!text-slate-600",
};

function CustomPanel({ from, to, setFrom, setTo }) {
  const [tf, setTf] = useState(timeOf(from));
  const [tt, setTt] = useState(timeOf(to));
  const months = typeof window !== "undefined" && window.innerWidth < 640 ? 1 : 2;
  const box = "flex items-center gap-2 px-3 py-2 rounded border border-slate-700 bg-slate-950 text-sm text-slate-100";
  const onSelect = (r) => {
    if (!r?.from) return;
    setFrom(withTime(r.from, tf));
    setTo(withTime(r.to || r.from, tt));
  };
  return (
    <div className="p-3 border-r border-slate-800" data-testid="date-range-custom-panel">
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <div className="space-y-2">
          <div className={box} data-testid="date-range-from-display"><CalIcon className="w-4 h-4 text-slate-400" />{format(from, "MMM d, yyyy HH:mm")}</div>
          <TimeSelect testid="date-range-from-time" value={tf} onChange={(t) => { setTf(t); setFrom(withTime(from, t)); }} />
        </div>
        <div className="space-y-2">
          <div className={box} data-testid="date-range-to-display"><CalIcon className="w-4 h-4 text-slate-400" />{format(to, "MMM d, yyyy HH:mm")}</div>
          <TimeSelect testid="date-range-to-time" value={tt} onChange={(t) => { setTt(t); setTo(withTime(to, t)); }} />
        </div>
      </div>
      <DayPicker mode="range" numberOfMonths={months} showOutsideDays defaultMonth={from}
        selected={{ from, to }} onSelect={onSelect} classNames={dayClasses} />
    </div>
  );
}

export function DateRangeFilter({ value, onChange, testid = "date-range", align = "left" }) {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState(value?.preset || "today");
  const [from, setFrom] = useState(value?.from || startOfDay(new Date()));
  const [to, setTo] = useState(value?.to || endOfDay(new Date()));
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const openPanel = () => {
    setPreset(value?.preset || "today");
    setFrom(value?.from || startOfDay(new Date()));
    setTo(value?.to || endOfDay(new Date()));
    setOpen((o) => !o);
  };
  const pick = (key, fn) => {
    setPreset(key);
    if (fn) { const [a, b] = fn(); setFrom(a); setTo(b); }
  };
  const apply = () => {
    const p = PRESETS.find((x) => x[0] === preset);
    const [a, b] = p[2] ? p[2]() : [from, to];
    onChange({ preset, from: a, to: b });
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <div className="flex items-center gap-1">
        <button type="button" data-testid={`${testid}-trigger`} onClick={openPanel}
          className="inline-flex items-center gap-2 px-3 py-2.5 rounded-lg border border-slate-700 bg-slate-950/60 text-xs font-mono tracking-wider text-slate-200 hover:border-slate-500 transition-colors">
          <CalIcon className="w-4 h-4 text-slate-400" />
          <span data-testid={`${testid}-label`}>{value ? `${fmtLabel(value.from)} - ${fmtLabel(value.to)}` : "ALL TIME"}</span>
          <ChevronDown className="w-4 h-4 text-slate-400" />
        </button>
        {value && (
          <button type="button" data-testid={`${testid}-clear`} onClick={() => onChange(null)} title="Clear date filter"
            className="p-1.5 text-slate-500 hover:text-slate-200"><X className="w-4 h-4" /></button>
        )}
      </div>
      {open && (
        <div data-testid={`${testid}-panel`}
          className={`absolute z-50 mt-2 ${align === "right" ? "right-0" : "left-0"} max-w-[calc(100vw-2rem)] overflow-auto flex flex-col-reverse sm:flex-row rounded-lg border border-slate-700 bg-slate-900 shadow-2xl shadow-black/50`}>
          {preset === "custom" && <CustomPanel from={from} to={to} setFrom={setFrom} setTo={setTo} />}
          <div className="p-2 w-full sm:w-44 shrink-0 flex flex-col">
            {PRESETS.map(([key, label, fn]) => (
              <button key={key} type="button" data-testid={`${testid}-preset-${key}`} onClick={() => pick(key, fn)}
                className={`text-left px-3 py-1.5 rounded text-sm transition-colors ${preset === key ? "bg-indigo-600 text-white" : "text-slate-200 hover:bg-slate-800"}`}>
                {label}
              </button>
            ))}
            <div className="flex gap-2 mt-3 pt-3 border-t border-slate-800">
              <button type="button" data-testid={`${testid}-apply`} onClick={apply}
                className="flex-1 px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold tracking-wider">APPLY</button>
              <button type="button" data-testid={`${testid}-cancel`} onClick={() => setOpen(false)}
                className="flex-1 px-3 py-1.5 rounded border border-rose-400/70 text-slate-200 hover:bg-rose-500/10 text-xs font-semibold tracking-wider">CANCEL</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
